/**
 * =============================================================================
 * ===== FILE: ant.js — ENTITY SEMUT (GERAK RNG + GAMBAR + SKIN) =====
 * =============================================================================
 * - updateRace()  : kecepatan acak murni (bukan skill)
 * - draw()        : body, kaki, mata, antena, skin kosmetik, name tag
 * - drawAntPreview() : thumbnail di kartu menu
 * =============================================================================
 */

import { CONFIG, rand, clamp, getTrackPadding, computeAntScale } from './config.js';
import { colorCss, getSkin } from './skins.js';

/**
 * Ant entity — cosmetic animation + pure-RNG movement state.
 * No stats affect win chance beyond ephemeral random speed fields.
 */
export class Ant {
  /**
   * constructor(data, laneIndex, laneCount)
   * data: { id, name, skinId, color, points? }
   */
  constructor(data, laneIndex, laneCount) {
    this.id = data.id;
    this.name = data.name;
    this.skinId = data.skinId;
    this.color = { ...data.color };
    this.laneIndex = laneIndex;
    this.laneCount = laneCount;

    this.x = 40;
    this.y = 0;
    this.progress = 0;
    this.finished = false;
    this.finishTime = null;
    this.eliminated = false;
    this.rank = 0;
    this.points = data.points || 0;

    // RNG movement — re-rolled continuously, no persistent advantage
    this.baseSpeed = rand(CONFIG.SPEED.min, CONFIG.SPEED.max);
    this.phase = rand(0, Math.PI * 2);
    this.freq = rand(CONFIG.SPEED.jitterFreqMin, CONFIG.SPEED.jitterFreqMax);
    this.amp = rand(CONFIG.SPEED.jitterAmp * 0.6, CONFIG.SPEED.jitterAmp);
    this.effectTimer = 0;
    this.effectMult = 1;
    this.speed = this.baseSpeed;

    // Animation
    this.walk = rand(0, Math.PI * 2);
    this.blinkT = rand(1, 4);
    this.blink = 0;
    this.antenna = rand(0, Math.PI * 2);
    this.bounce = 0;
    this.dustCD = 0;
    this.facing = 1;
    this.scale = 1;

    this._recomputeY();
  }

  _recomputeY() {
    const h = this.trackHeight || 600;
    const w = this.trackWidth || 390;
    const pad = getTrackPadding(h);
    const usable = Math.max(140, h - pad * 2);
    const lanes = Math.max(2, this.laneCount);
    const laneH = usable / lanes;
    this.y = pad + laneH * (this.laneIndex + 0.5);
    this.scale = computeAntScale(h, w, lanes);
    // Keep body inside lane band when scale is large
    const maxByLane = (laneH * 0.92) / 36;
    if (this.scale > maxByLane && maxByLane > 0.6) {
      this.scale = Math.max(0.7, Math.min(this.scale, maxByLane));
    }
  }

  setTrackLayout(height, laneCount, width) {
    this.trackHeight = height;
    this.laneCount = laneCount;
    if (width != null) this.trackWidth = width;
    this._recomputeY();
  }

  resetPosition() {
    this.x = 40;
    this.progress = 0;
    this.finished = false;
    this.finishTime = null;
    this.eliminated = false;
    this.rank = 0;
    this.baseSpeed = rand(CONFIG.SPEED.min, CONFIG.SPEED.max);
    this.phase = rand(0, Math.PI * 2);
    this.freq = rand(CONFIG.SPEED.jitterFreqMin, CONFIG.SPEED.jitterFreqMax);
    this.amp = rand(CONFIG.SPEED.jitterAmp * 0.6, CONFIG.SPEED.jitterAmp);
    this.effectTimer = 0;
    this.effectMult = 1;
    this.speed = this.baseSpeed;
  }

  /**
   * updateRace(dt, trackLength, racing)
   * Pure RNG speed update — no skill/stats.
   * Jitter + burst/slow acak; set finished saat x >= trackLength.
   */
  updateRace(dt, trackLength, racing) {
    if (this.eliminated || this.finished || !racing) {
      this.updateAnim(dt, 0);
      return;
    }

    // Continuous organic jitter
    this.phase += dt * this.freq;
    let mult = 1 + Math.sin(this.phase) * (this.amp / Math.max(this.baseSpeed, 1));

    // Random bursts / slows — temporary, fair for all
    if (this.effectTimer > 0) {
      this.effectTimer -= dt;
      mult *= this.effectMult;
    } else {
      const r = Math.random();
      if (r < CONFIG.SPEED.burstChance) {
        this.effectMult = CONFIG.SPEED.burstMult * rand(0.9, 1.15);
        this.effectTimer = rand(...CONFIG.SPEED.effectDuration);
      } else if (r < CONFIG.SPEED.burstChance + CONFIG.SPEED.slowChance) {
        this.effectMult = CONFIG.SPEED.slowMult * rand(0.85, 1.1);
        this.effectTimer = rand(...CONFIG.SPEED.effectDuration);
      } else if (Math.random() < 0.008) {
        // occasional base re-roll mid-race for comebacks
        this.baseSpeed = rand(CONFIG.SPEED.min, CONFIG.SPEED.max);
        this.freq = rand(CONFIG.SPEED.jitterFreqMin, CONFIG.SPEED.jitterFreqMax);
      }
    }

    this.speed = clamp(this.baseSpeed * mult, 40, 280);
    this.x += this.speed * dt;
    this.progress = clamp(this.x / trackLength, 0, 1);

    if (this.x >= trackLength) {
      this.x = trackLength;
      this.progress = 1;
      this.finished = true;
    }

    this.updateAnim(dt, this.speed);
  }

  /**
   * updateAnim(dt, speed)
   * Animasi kosmetik: kaki (walk phase), antena, kedip, bounce.
   */
  updateAnim(dt, speed) {
    const walkSpeed = 8 + (speed / 40);
    this.walk += dt * walkSpeed;
    this.antenna += dt * 5;
    this.bounce = Math.sin(this.walk * 2) * 2.2 * this.scale;

    this.blinkT -= dt;
    if (this.blinkT <= 0) {
      this.blink = 0.12;
      this.blinkT = rand(1.5, 4.5);
    }
    if (this.blink > 0) this.blink -= dt;

    this.dustCD -= dt;
  }

  /**
   * draw(ctx, particles, camera, opts)
   * Gambar semut di world-space (lazy: skip jika di luar kamera).
   * opts: { isLeader, nearCam, showTag }
   */
  draw(ctx, particles, camera, opts = {}) {
    if (this.eliminated) return;
    if (!camera.inView(this.x, 100)) return;

    const s = this.scale;
    const x = this.x;
    const y = this.y + this.bounce;
    const dense = this.laneCount > 18;
    const showTag =
      opts.showTag !== false &&
      (!dense || opts.isLeader || opts.nearCam || this.finished);

    // Dust (cap in dense races)
    if (!dense && this.speed > 30 && this.dustCD <= 0 && !this.finished) {
      particles.spawnDust(x - 10 * s, y + 8 * s, this.speed > 160 ? 3 : 1);
      this.dustCD = this.speed > 160 ? 0.04 : 0.08;
    } else if (dense && this.speed > 80 && this.dustCD <= 0 && !this.finished && Math.random() < 0.3) {
      particles.spawnDust(x - 10 * s, y + 8 * s, 1);
      this.dustCD = 0.12;
    }

    ctx.save();
    ctx.translate(x, y);
    const lean = clamp(this.speed / 400, 0, 0.18);
    ctx.rotate(-lean);

    ctx.fillStyle = 'rgba(0,0,0,0.22)';
    ctx.beginPath();
    ctx.ellipse(0, 12 * s, 16 * s, 4.5 * s, 0, 0, Math.PI * 2);
    ctx.fill();

    this._drawLegs(ctx, s);
    this._drawBody(ctx, s);
    this._drawHead(ctx, s);
    this._drawSkin(ctx, s);
    if (showTag) this._drawNameTag(ctx, s);

    ctx.restore();
  }

  /** Gambar 3 pasang kaki berayun. */
  _drawLegs(ctx, s) {
    const c = colorCss(this.color, -12, 5);
    ctx.strokeStyle = c;
    ctx.lineWidth = 2 * s;
    ctx.lineCap = 'round';
    const w = this.walk;
    for (let i = 0; i < 3; i++) {
      const side = i % 2 === 0 ? 1 : -1;
      const ox = (-6 + i * 7) * s;
      const swing = Math.sin(w + i * 1.1) * 7 * s * side;
      const swing2 = Math.sin(w + i * 1.1 + 0.4) * 5 * s * side;
      // left-ish
      ctx.beginPath();
      ctx.moveTo(ox, 2 * s);
      ctx.quadraticCurveTo(ox - 8 * s + swing * 0.3, 8 * s, ox - 12 * s + swing, 14 * s);
      ctx.stroke();
      // right-ish
      ctx.beginPath();
      ctx.moveTo(ox, 2 * s);
      ctx.quadraticCurveTo(ox + 8 * s - swing2 * 0.3, 8 * s, ox + 12 * s - swing2, 14 * s);
      ctx.stroke();
    }
  }

  /** Gambar abdomen + thorax. */
  _drawBody(ctx, s) {
    const body = colorCss(this.color);
    const dark = colorCss(this.color, -15);
    const light = colorCss(this.color, 12);

    // abdomen
    const grd = ctx.createRadialGradient(2 * s, -2 * s, 2 * s, 0, 0, 16 * s);
    grd.addColorStop(0, light);
    grd.addColorStop(1, body);
    ctx.fillStyle = grd;
    ctx.beginPath();
    ctx.ellipse(-6 * s, 0, 12 * s, 9 * s, 0, 0, Math.PI * 2);
    ctx.fill();

    // segment line
    ctx.strokeStyle = dark;
    ctx.lineWidth = 1 * s;
    ctx.beginPath();
    ctx.ellipse(-6 * s, 0, 7 * s, 6 * s, 0, -0.6, 0.6);
    ctx.stroke();

    // thorax
    ctx.fillStyle = body;
    ctx.beginPath();
    ctx.ellipse(6 * s, 0, 7 * s, 6.5 * s, 0, 0, Math.PI * 2);
    ctx.fill();

    // highlight
    ctx.fillStyle = colorCss(this.color, 22, -10, 0.35);
    ctx.beginPath();
    ctx.ellipse(4 * s, -3 * s, 4 * s, 2.5 * s, -0.4, 0, Math.PI * 2);
    ctx.fill();
  }

  /** Gambar kepala, antena, mata berkedip, senyum. */
  _drawHead(ctx, s) {
    const body = colorCss(this.color, 5);
    const dark = colorCss(this.color, -20);

    // head
    ctx.fillStyle = body;
    ctx.beginPath();
    ctx.ellipse(16 * s, -2 * s, 7.5 * s, 7 * s, 0, 0, Math.PI * 2);
    ctx.fill();

    // antennas
    ctx.strokeStyle = dark;
    ctx.lineWidth = 1.6 * s;
    ctx.lineCap = 'round';
    const a1 = Math.sin(this.antenna) * 0.25;
    const a2 = Math.sin(this.antenna + 1.2) * 0.25;
    ctx.beginPath();
    ctx.moveTo(18 * s, -7 * s);
    ctx.quadraticCurveTo(22 * s, -16 * s + a1 * 10 * s, 26 * s, -18 * s + a1 * 12 * s);
    ctx.stroke();
    ctx.beginPath();
    ctx.moveTo(20 * s, -6 * s);
    ctx.quadraticCurveTo(26 * s, -14 * s + a2 * 10 * s, 30 * s, -15 * s + a2 * 12 * s);
    ctx.stroke();
    ctx.fillStyle = colorCss(this.color, 20, 10);
    ctx.beginPath();
    ctx.arc(26 * s, -18 * s + a1 * 12 * s, 1.8 * s, 0, Math.PI * 2);
    ctx.arc(30 * s, -15 * s + a2 * 12 * s, 1.8 * s, 0, Math.PI * 2);
    ctx.fill();

    // eyes
    const eyeOpen = this.blink > 0 ? 0.15 : 1;
    ctx.fillStyle = '#1a120b';
    ctx.beginPath();
    ctx.ellipse(18 * s, -3 * s, 2.2 * s, 2.2 * s * eyeOpen, 0, 0, Math.PI * 2);
    ctx.ellipse(22 * s, -3.5 * s, 2.4 * s, 2.4 * s * eyeOpen, 0, 0, Math.PI * 2);
    ctx.fill();
    if (eyeOpen > 0.5) {
      ctx.fillStyle = '#fff';
      ctx.beginPath();
      ctx.arc(18.6 * s, -3.6 * s, 0.7 * s, 0, Math.PI * 2);
      ctx.arc(22.7 * s, -4.1 * s, 0.8 * s, 0, Math.PI * 2);
      ctx.fill();
    }

    // smile mandibles
    ctx.strokeStyle = dark;
    ctx.lineWidth = 1.2 * s;
    ctx.beginPath();
    ctx.arc(20 * s, 0 * s, 3 * s, 0.15, Math.PI - 0.15);
    ctx.stroke();
  }

  /**
   * _drawSkin(ctx, s)
   * Aksesoris kosmetik (topi, sayap, dll.) — tidak mempengaruhi balapan.
   */
  _drawSkin(ctx, s) {
    const skin = this.skinId;
    const hx = 16 * s;
    const hy = -2 * s;

    ctx.save();
    switch (skin) {
      case 'cowboy':
        ctx.fillStyle = '#8B5A2B';
        ctx.beginPath();
        ctx.ellipse(hx, hy - 8 * s, 11 * s, 3 * s, 0, 0, Math.PI * 2);
        ctx.fill();
        ctx.fillRect(hx - 5 * s, hy - 14 * s, 10 * s, 7 * s);
        break;
      case 'police':
        ctx.fillStyle = '#1d3557';
        ctx.fillRect(hx - 7 * s, hy - 12 * s, 14 * s, 7 * s);
        ctx.fillStyle = '#e63946';
        ctx.fillRect(hx - 2 * s, hy - 12 * s, 4 * s, 7 * s);
        ctx.fillStyle = '#ffd166';
        ctx.beginPath();
        ctx.arc(hx, hy - 12 * s, 1.5 * s, 0, Math.PI * 2);
        ctx.fill();
        break;
      case 'chef':
        ctx.fillStyle = '#f8f9fa';
        ctx.beginPath();
        ctx.ellipse(hx, hy - 10 * s, 8 * s, 6 * s, 0, 0, Math.PI * 2);
        ctx.fill();
        ctx.fillRect(hx - 5 * s, hy - 8 * s, 10 * s, 4 * s);
        break;
      case 'pirate':
        ctx.fillStyle = '#111';
        ctx.beginPath();
        ctx.ellipse(hx + 2 * s, hy - 3 * s, 5 * s, 3.2 * s, -0.2, 0, Math.PI * 2);
        ctx.fill();
        ctx.strokeStyle = '#c1121f';
        ctx.lineWidth = 2 * s;
        ctx.beginPath();
        ctx.moveTo(hx - 2 * s, hy + 4 * s);
        ctx.lineTo(hx + 10 * s, hy + 8 * s);
        ctx.stroke();
        break;
      case 'viking':
        ctx.fillStyle = '#6c757d';
        ctx.beginPath();
        ctx.arc(hx, hy - 8 * s, 8 * s, Math.PI, 0);
        ctx.fill();
        ctx.fillStyle = '#f8f9fa';
        ctx.beginPath();
        ctx.moveTo(hx - 8 * s, hy - 8 * s);
        ctx.lineTo(hx - 12 * s, hy - 16 * s);
        ctx.lineTo(hx - 4 * s, hy - 10 * s);
        ctx.fill();
        ctx.beginPath();
        ctx.moveTo(hx + 8 * s, hy - 8 * s);
        ctx.lineTo(hx + 12 * s, hy - 16 * s);
        ctx.lineTo(hx + 4 * s, hy - 10 * s);
        ctx.fill();
        break;
      case 'astronaut':
        ctx.strokeStyle = 'rgba(200,220,255,0.7)';
        ctx.lineWidth = 2 * s;
        ctx.beginPath();
        ctx.arc(hx + 2 * s, hy, 10 * s, 0, Math.PI * 2);
        ctx.stroke();
        ctx.fillStyle = 'rgba(180,210,255,0.15)';
        ctx.fill();
        break;
      case 'ninja':
        ctx.fillStyle = '#222';
        ctx.fillRect(hx - 8 * s, hy - 6 * s, 16 * s, 5 * s);
        ctx.fillStyle = '#e63946';
        ctx.fillRect(hx - 8 * s, hy - 4 * s, 16 * s, 1.5 * s);
        break;
      case 'santa':
        ctx.fillStyle = '#e63946';
        ctx.beginPath();
        ctx.moveTo(hx - 8 * s, hy - 6 * s);
        ctx.lineTo(hx + 2 * s, hy - 18 * s);
        ctx.lineTo(hx + 8 * s, hy - 6 * s);
        ctx.fill();
        ctx.fillStyle = '#fff';
        ctx.beginPath();
        ctx.arc(hx + 2 * s, hy - 18 * s, 2.5 * s, 0, Math.PI * 2);
        ctx.fill();
        ctx.fillRect(hx - 8 * s, hy - 7 * s, 16 * s, 3 * s);
        break;
      case 'grad':
        ctx.fillStyle = '#212529';
        ctx.fillRect(hx - 9 * s, hy - 10 * s, 18 * s, 3 * s);
        ctx.beginPath();
        ctx.moveTo(hx - 6 * s, hy - 10 * s);
        ctx.lineTo(hx, hy - 16 * s);
        ctx.lineTo(hx + 6 * s, hy - 10 * s);
        ctx.fill();
        ctx.strokeStyle = '#ffd166';
        ctx.lineWidth = 1.5 * s;
        ctx.beginPath();
        ctx.moveTo(hx + 6 * s, hy - 10 * s);
        ctx.lineTo(hx + 10 * s, hy - 2 * s);
        ctx.stroke();
        break;
      case 'king':
      case 'crown':
        ctx.fillStyle = '#ffd166';
        ctx.beginPath();
        ctx.moveTo(hx - 8 * s, hy - 6 * s);
        ctx.lineTo(hx - 6 * s, hy - 14 * s);
        ctx.lineTo(hx - 2 * s, hy - 8 * s);
        ctx.lineTo(hx, hy - 16 * s);
        ctx.lineTo(hx + 2 * s, hy - 8 * s);
        ctx.lineTo(hx + 6 * s, hy - 14 * s);
        ctx.lineTo(hx + 8 * s, hy - 6 * s);
        ctx.closePath();
        ctx.fill();
        ctx.fillStyle = '#ef476f';
        ctx.beginPath();
        ctx.arc(hx, hy - 16 * s, 1.6 * s, 0, Math.PI * 2);
        ctx.fill();
        break;
      case 'wizard':
        ctx.fillStyle = '#7b2cbf';
        ctx.beginPath();
        ctx.moveTo(hx - 7 * s, hy - 5 * s);
        ctx.lineTo(hx + 1 * s, hy - 20 * s);
        ctx.lineTo(hx + 8 * s, hy - 5 * s);
        ctx.fill();
        ctx.fillStyle = '#ffd166';
        ctx.beginPath();
        ctx.arc(hx + 1 * s, hy - 12 * s, 1.4 * s, 0, Math.PI * 2);
        ctx.fill();
        break;
      case 'superhero':
        ctx.fillStyle = '#e63946';
        ctx.beginPath();
        ctx.moveTo(-4 * s, -4 * s);
        ctx.quadraticCurveTo(-18 * s, -10 * s, -14 * s, 8 * s);
        ctx.quadraticCurveTo(-4 * s, 4 * s, -2 * s, 2 * s);
        ctx.fill();
        ctx.fillStyle = '#ffd166';
        ctx.font = `bold ${10 * s}px sans-serif`;
        ctx.textAlign = 'center';
        ctx.fillText('S', 6 * s, 4 * s);
        break;
      case 'hoodie':
        ctx.fillStyle = '#457b9d';
        ctx.beginPath();
        ctx.arc(hx, hy - 2 * s, 10 * s, Math.PI * 1.05, -0.05);
        ctx.fill();
        ctx.fillStyle = '#1d3557';
        ctx.fillRect(4 * s, -4 * s, 10 * s, 10 * s);
        break;
      case 'denim':
        ctx.fillStyle = '#4a6fa5';
        ctx.fillRect(0 * s, -6 * s, 12 * s, 12 * s);
        ctx.strokeStyle = '#ffd166';
        ctx.lineWidth = 1 * s;
        ctx.strokeRect(1 * s, -5 * s, 10 * s, 10 * s);
        break;
      case 'straw':
        ctx.fillStyle = '#e9c46a';
        ctx.beginPath();
        ctx.ellipse(hx, hy - 8 * s, 12 * s, 3 * s, 0, 0, Math.PI * 2);
        ctx.fill();
        ctx.fillRect(hx - 5 * s, hy - 14 * s, 10 * s, 7 * s);
        ctx.strokeStyle = '#bc6c25';
        ctx.lineWidth = 1.2 * s;
        ctx.beginPath();
        ctx.moveTo(hx - 5 * s, hy - 9 * s);
        ctx.lineTo(hx + 5 * s, hy - 9 * s);
        ctx.stroke();
        break;
      case 'hardhat':
        ctx.fillStyle = '#ffd166';
        ctx.beginPath();
        ctx.ellipse(hx, hy - 7 * s, 10 * s, 4 * s, 0, 0, Math.PI * 2);
        ctx.fill();
        ctx.beginPath();
        ctx.arc(hx, hy - 8 * s, 7 * s, Math.PI, 0);
        ctx.fill();
        break;
      case 'headphones':
        ctx.strokeStyle = '#222';
        ctx.lineWidth = 2.5 * s;
        ctx.beginPath();
        ctx.arc(hx, hy - 2 * s, 9 * s, Math.PI * 0.9, Math.PI * 0.1, true);
        ctx.stroke();
        ctx.fillStyle = '#ef476f';
        ctx.beginPath();
        ctx.ellipse(hx - 8 * s, hy, 3 * s, 4 * s, 0, 0, Math.PI * 2);
        ctx.ellipse(hx + 9 * s, hy, 3 * s, 4 * s, 0, 0, Math.PI * 2);
        ctx.fill();
        break;
      case 'glasses':
        ctx.strokeStyle = '#222';
        ctx.lineWidth = 1.5 * s;
        ctx.strokeRect(hx - 2 * s, hy - 5 * s, 6 * s, 5 * s);
        ctx.strokeRect(hx + 6 * s, hy - 5.5 * s, 6 * s, 5 * s);
        ctx.beginPath();
        ctx.moveTo(hx + 4 * s, hy - 2.5 * s);
        ctx.lineTo(hx + 6 * s, hy - 2.5 * s);
        ctx.stroke();
        break;
      case 'mustache':
        ctx.fillStyle = '#3d2914';
        ctx.beginPath();
        ctx.ellipse(hx + 2 * s, hy + 4 * s, 6 * s, 2.2 * s, 0, 0, Math.PI * 2);
        ctx.fill();
        break;
      case 'fairy':
        ctx.globalAlpha = 0.55;
        ctx.fillStyle = '#bde0fe';
        ctx.beginPath();
        ctx.ellipse(-10 * s, -8 * s, 8 * s, 12 * s, -0.4, 0, Math.PI * 2);
        ctx.ellipse(-6 * s, 2 * s, 7 * s, 10 * s, 0.3, 0, Math.PI * 2);
        ctx.fill();
        ctx.fillStyle = '#ffc8dd';
        ctx.beginPath();
        ctx.ellipse(-12 * s, -6 * s, 5 * s, 8 * s, -0.3, 0, Math.PI * 2);
        ctx.fill();
        ctx.globalAlpha = 1;
        break;
      case 'flower': {
        const petals = ['#ff8fab', '#ffc8dd', '#f72585', '#ffd6a5'];
        for (let i = 0; i < 6; i++) {
          const ang = (i / 6) * Math.PI * 2;
          ctx.fillStyle = petals[i % petals.length];
          ctx.beginPath();
          ctx.ellipse(
            hx + Math.cos(ang) * 7 * s,
            hy - 10 * s + Math.sin(ang) * 5 * s,
            4 * s,
            2.5 * s,
            ang,
            0,
            Math.PI * 2
          );
          ctx.fill();
        }
        ctx.fillStyle = '#ffd166';
        ctx.beginPath();
        ctx.arc(hx, hy - 10 * s, 2.5 * s, 0, Math.PI * 2);
        ctx.fill();
        break;
      }
      default:
        break;
    }
    ctx.restore();
  }

  _drawNameTag(ctx, s) {
    const maxLen = s < 0.9 ? 10 : 14;
    const label = this.name.length > maxLen ? this.name.slice(0, maxLen - 1) + '…' : this.name;
    // Keep labels readable on phones even if body scale is moderate
    const fontPx = clamp(12 * Math.max(s, 1.05), 11, 18);
    ctx.font = `bold ${fontPx}px Nunito, sans-serif`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'bottom';
    const tw = ctx.measureText(label).width;
    const padX = Math.max(6, 5 * s);
    const tagH = Math.max(16, 15 * Math.max(s, 1));
    const bx = 4 * s;
    const by = -20 * Math.max(s, 1.05);
    ctx.fillStyle = 'rgba(0,0,0,0.55)';
    roundRect(ctx, bx - tw / 2 - padX, by - tagH, tw + padX * 2, tagH, 7);
    ctx.fill();
    // outline for contrast on bright dirt
    ctx.strokeStyle = 'rgba(255,255,255,0.18)';
    ctx.lineWidth = 1;
    roundRect(ctx, bx - tw / 2 - padX, by - tagH, tw + padX * 2, tagH, 7);
    ctx.stroke();
    ctx.fillStyle = '#fff';
    ctx.fillText(label, bx, by - 3);

    ctx.fillStyle = colorCss(this.color, 5);
    ctx.beginPath();
    ctx.arc(bx - tw / 2 - padX + 7, by - tagH / 2, 3.2, 0, Math.PI * 2);
    ctx.fill();
  }
}

function roundRect(ctx, x, y, w, h, r) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}

/** Preview sprite for menu cards — sized to canvas, full body visible */
export function drawAntPreview(canvas, antData) {
  const ctx = canvas.getContext('2d');
  const dpr = Math.min(window.devicePixelRatio || 1, 2);
  // Prefer CSS pixel box; fallback larger for mobile clarity
  const cssW = Math.max(64, Math.round(canvas.clientWidth || 72));
  const cssH = Math.max(64, Math.round(canvas.clientHeight || 72));
  canvas.width = cssW * dpr;
  canvas.height = cssH * dpr;
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  ctx.clearRect(0, 0, cssW, cssH);

  const fake = new Ant(antData, 0, 1);
  // Fit whole ant (antenna ~ -20, legs ~ +14, body width ~ 40 units at s=1)
  const fit = Math.min(cssW / 56, cssH / 48);
  fake.scale = clamp(fit * 1.05, 1.15, 2.0);
  fake.walk = performance.now() / 180;
  fake.antenna = performance.now() / 200;
  fake.bounce = Math.sin(fake.walk * 2) * 1.2;
  fake.y = 0;
  fake.x = 0;

  const s = fake.scale;
  ctx.save();
  // Center body in the preview box
  ctx.translate(cssW * 0.42, cssH * 0.58);
  ctx.fillStyle = 'rgba(0,0,0,0.15)';
  ctx.beginPath();
  ctx.ellipse(0, 10 * s, 14 * s, 4 * s, 0, 0, Math.PI * 2);
  ctx.fill();
  fake._drawLegs(ctx, s);
  fake._drawBody(ctx, s);
  fake._drawHead(ctx, s);
  fake._drawSkin(ctx, s);
  ctx.restore();

  const skin = getSkin(antData.skinId);
  ctx.font = `${Math.max(12, Math.round(cssW * 0.18))}px sans-serif`;
  ctx.textAlign = 'right';
  ctx.fillStyle = 'rgba(255,255,255,0.9)';
  ctx.fillText(skin.emoji === 'hoodie' ? '👕' : skin.emoji, cssW - 4, 16);
}
